<template>
  <div>
    <a-form class="simiot-nested-search-form" :form="form" @submit="handleSearch">
      <a-row :gutter="24">
        <a-col :span="6">
          <a-form-item label="账期">
            <a-month-picker
              v-decorator="['carrier_month']"
              placeholder="全部账期"
              allow-clear
              :disabled-date="disabledDate"
            />
          </a-form-item>
        </a-col>
        <a-col :span="6">
          <a-form-item label="维度">
            <a-select v-model="dimension" @change="onDimensionChange">
              <a-select-option value="carrier_product">运营商</a-select-option>
              <a-select-option value="agent_product">客户</a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <template v-if="dimension === 'carrier_product'">
          <a-col :span="6">
            <a-form-item label="运营商账户">
              <a-select
                show-search
                allow-clear
                option-filter-prop="children"
                :loading="loadingCarrierAccountOptions"
                :value="filterCarrierAccountId"
                placeholder="全部账户"
                @change="changeFilterCarrierAccount"
              >
                <a-select-option v-for="item in carrierAccountOptions" :key="item.id" :value="item.id">
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="运营商套餐">
              <a-select
                show-search
                allow-clear
                option-filter-prop="children"
                :disabled="!filterCarrierAccountId"
                :loading="loadingCarrierProductOptions"
                :value="filterCarrierProductId"
                :placeholder="filterCarrierAccountId ? '全部套餐' : '请先选择运营商账户'"
                @change="changeFilterCarrierProduct"
              >
                <a-select-option v-for="item in carrierProductOptions" :key="item.id" :value="item.id">
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </template>
        <template v-else>
          <a-col :span="6">
            <a-form-item label="客户">
              <a-select
                show-search
                allow-clear
                :filter-option="false"
                :value="filterTopAgentId"
                placeholder="搜索一级客户"
                @search="searchFilterAgents"
                @change="changeFilterAgent"
              >
                <a-select-option v-for="item in filterAgentOptions" :key="item.id" :value="item.id">
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="客户套餐">
              <a-select
                show-search
                allow-clear
                option-filter-prop="children"
                :disabled="!filterTopAgentId"
                :loading="loadingProductOptions"
                :value="filterProductId"
                :placeholder="filterTopAgentId ? '全部套餐' : '请先选择客户'"
                @change="changeFilterProduct"
              >
                <a-select-option v-for="item in productOptions" :key="item.id" :value="item.product_id">
                  {{ item.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </template>
        <a-col :span="6">
          <a-form-item>
            <a-space>
              <a-button type="primary" html-type="submit">搜索</a-button>
              <a-button html-type="button" @click="exportData">导出</a-button>
            </a-space>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>

    <a-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      :pagination="false"
      :locale="{ emptyText: emptyText }"
      row-key="rowKey"
      size="middle"
    />
  </div>
</template>

<script>
import { findSimCardMonthCostStatistics, exportSimCardMonthCostStatistics } from '@/api/sim_card_month_cost'
import { exportExcel } from '@/api/excel'
import { findAgentOptions } from '@/api/agent'
import { findAgentsProductsOptions } from '@/api/agents_product'
import { findCarrierAccountProductOptions } from '@/api/carrier_product'
import { findAllCarrierAccountOptions } from '@/api/common_options'
import { formatYearMonth } from '@/utils/time'

const carrierProduct = 'carrier_product'
const agentProduct = 'agent_product'

export default {
  name: 'SimCardMonthCostStatistics',
  data() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      searched: false,
      dimension: carrierProduct,
      tableData: [],
      filterCarrierAccountId: undefined,
      carrierAccountOptions: [],
      loadingCarrierAccountOptions: false,
      filterCarrierProductId: undefined,
      carrierProductOptions: [],
      loadingCarrierProductOptions: false,
      filterTopAgentId: undefined,
      filterAgentOptions: [],
      filterProductId: undefined,
      productOptions: [],
      loadingProductOptions: false,
      agentSearchKeyword: ''
    }
  },
  computed: {
    emptyText() {
      return this.searched ? '暂无数据' : '请搜索'
    },
    columns() {
      const groupTitle = this.dimension === agentProduct ? '客户' : '运营商账户'
      const itemTitle = this.dimension === agentProduct ? '客户套餐' : '运营商套餐'
      return [
        { title: '账期月', dataIndex: 'carrier_month' },
        { title: groupTitle, dataIndex: 'group_name' },
        { title: itemTitle, dataIndex: 'item_name' },
        { title: '成本合计', dataIndex: 'cost_amount' }
      ]
    }
  },
  created() {
    this.fetchCarrierAccountOptions()
    this.load('')
  },
  methods: {
    disabledDate(current) {
      return current && current.clone().endOf('month') >= this.$moment().endOf('month')
    },
    handleSearch(e) {
      e.preventDefault()
      this.load(formatYearMonth(this.form.getFieldValue('carrier_month')))
    },
    exportData() {
      const carrierMonth = formatYearMonth(this.form.getFieldValue('carrier_month'))
      exportSimCardMonthCostStatistics(this.currentFilters(carrierMonth)).then((res) => {
        if (res.code === 0 && res.data.is_direct_download) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    },
    onDimensionChange() {
      this.$nextTick(() => {
        if (!this.searched) {
          this.tableData = []
          return
        }
        this.load(formatYearMonth(this.form.getFieldValue('carrier_month')))
      })
    },
    currentFilters(carrierMonth) {
      const query = {
        dimension: this.dimension
      }
      if (carrierMonth) {
        query.carrier_month = carrierMonth
      }
      if (this.dimension === carrierProduct) {
        if (this.filterCarrierAccountId) {
          query.carrier_account_id = this.filterCarrierAccountId
        }
        if (this.filterCarrierProductId) {
          query.carrier_product_id = this.filterCarrierProductId
        }
        return query
      }
      if (this.filterTopAgentId) {
        query.top_agent_id = this.filterTopAgentId
      }
      if (this.filterProductId) {
        query.product_id = this.filterProductId
      }
      return query
    },
    fetchCarrierAccountOptions() {
      this.loadingCarrierAccountOptions = true
      findAllCarrierAccountOptions().then((res) => {
        if (res.code === 0) {
          this.carrierAccountOptions = res.data || []
        }
        this.loadingCarrierAccountOptions = false
      }).catch(() => {
        this.loadingCarrierAccountOptions = false
      })
    },
    changeFilterCarrierAccount(value) {
      this.filterCarrierProductId = undefined
      this.carrierProductOptions = []
      const carrierAccountId = Number(value)
      if (!carrierAccountId) {
        this.filterCarrierAccountId = undefined
        return
      }
      this.filterCarrierAccountId = carrierAccountId
      this.fetchCarrierProductOptions(carrierAccountId)
    },
    changeFilterCarrierProduct(value) {
      if (value === undefined || value === null || value === '') {
        this.filterCarrierProductId = undefined
        return
      }
      this.filterCarrierProductId = value
    },
    fetchCarrierProductOptions(carrierAccountId) {
      this.loadingCarrierProductOptions = true
      findCarrierAccountProductOptions(carrierAccountId).then((res) => {
        if (res.code === 0 && Number(this.filterCarrierAccountId) === carrierAccountId) {
          this.carrierProductOptions = res.data || []
        }
        this.loadingCarrierProductOptions = false
      }).catch(() => {
        this.loadingCarrierProductOptions = false
      })
    },
    searchFilterAgents(name) {
      const keyword = this.$lodash.trim(name)
      this.agentSearchKeyword = keyword
      if (!keyword) {
        this.filterAgentOptions = []
        return
      }
      findAgentOptions({ name: keyword }).then((res) => {
        if (res.code === 0 && this.agentSearchKeyword === keyword) {
          this.filterAgentOptions = res.data || []
        }
      })
    },
    changeFilterAgent(value) {
      this.filterProductId = undefined
      this.productOptions = []
      if (value === undefined || value === null || value === '') {
        this.filterTopAgentId = undefined
        return
      }
      this.filterTopAgentId = value
      this.fetchProductOptions(value)
    },
    changeFilterProduct(value) {
      if (value === undefined || value === null || value === '') {
        this.filterProductId = undefined
        return
      }
      this.filterProductId = value
    },
    fetchProductOptions(agentId) {
      this.loadingProductOptions = true
      findAgentsProductsOptions({ agent_id: agentId, include_ineffective: true }).then((res) => {
        if (res.code === 0 && this.filterTopAgentId === agentId) {
          this.productOptions = res.data || []
        }
        this.loadingProductOptions = false
      }).catch(() => {
        this.loadingProductOptions = false
      })
    },
    load(carrierMonth) {
      this.loading = true
      findSimCardMonthCostStatistics(this.currentFilters(carrierMonth)).then((res) => {
        const isAgent = this.dimension === agentProduct
        this.tableData = (res.data || []).map((row) => {
          const groupId = isAgent ? row.top_agent_id : row.carrier_account_id
          const itemId = isAgent ? row.product_id : row.carrier_product_id
          return {
            rowKey: `${row.carrier_month}-${groupId}-${itemId}`,
            carrier_month: row.carrier_month,
            group_name: isAgent ? row.agent_name : row.carrier_account_name,
            item_name: isAgent ? row.product_name : row.carrier_product_name,
            cost_amount: row.cost_amount
          }
        })
        this.searched = true
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    }
  }
}
</script>
